import { ORG_BILLING_ENABLED } from "@/lib/featureFlags";
import { useAuthStore } from "@/store";
import {
  Brain,
  FileUp,
  Image,
  Layers,
  Loader2,
  Search,
  Settings,
  Share2,
  Sparkles,
  Video,
  Zap,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

// Feature icons mapping
const FeatureIcons = {
  models: Sparkles,
  files: FileUp,
  research: Search,
  image: Image,
  memory: Brain,
  projects: Layers,
  video: Video,
  settings: Settings,
  share: Share2,
  priority: Zap,
};

// Define pricing plan data - matching CheckoutPage design
const PLANS = {
  PRO: {
    id: "pro",
    name: "Pro",
    price: {
      monthly: 19.99,
      yearly: 17.99,
    },
    description: "For professionals and creators",
    tagline: "Get started today:",
    features: [
      { text: "Unlimited Premium Model Access", icon: "models" },
      { text: "Unlimited File Uploads", icon: "files" },
      { text: "Unlimited Deep Research", icon: "research" },
      { text: "Access to Premium Image Gen models", icon: "image" },
      { text: "Expanded memory and context", icon: "memory" },
      { text: "Unlimited Projects and Tasks", icon: "projects" },
      { text: "Limited video creation", icon: "video" },
    ],
    highlight: true,
    popular: true,
  },
  TEAM: {
    id: "team",
    name: "Team",
    price: {
      monthly: 29.99,
      yearly: 26.99,
    },
    description: "Perfect for teams and organizations",
    tagline: "Everything in Pro +",
    features: [
      { text: "Everything in Pro, plus:", icon: "models" },
      { text: "Enhanced research capabilities", icon: "research" },
      { text: "Unlimited shared projects", icon: "share" },
      { text: "Team management and controls", icon: "settings" },
      { text: "Priority access to best AI models", icon: "priority" },
    ],
    highlight: false,
    popular: false,
  },
};

// PricingCard component
const PricingCard = ({ plan, billingCycle, onClick, isLoading }) => {
  const price =
    billingCycle === "yearly" ? plan.price.yearly : plan.price.monthly;

  const isTeamPlan = plan.id === "team";
  const isTeamDisabled = isTeamPlan && !ORG_BILLING_ENABLED;
  const isProPlan = plan.id === "pro";

  return (
    <div
      className={`relative rounded-2xl p-6 h-full overflow-hidden transition-all duration-300 ${
        isProPlan
          ? "bg-gradient-to-br from-purple-500 via-purple-600 to-violet-600"
          : "bg-gray-900 dark:bg-gray-800 border border-gray-700"
      }`}
    >
      {/* Plan name with inline Popular badge */}
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-xl font-semibold text-white">{plan.name}</h3>
        {plan.popular && (
          <span className="inline-block bg-black/30 text-white text-xs font-medium px-3 py-1 rounded-full">
            Popular
          </span>
        )}
      </div>

      {/* Price */}
      <div className="mb-2">
        <div className="flex items-baseline">
          <span className="text-4xl font-bold text-white">${price}</span>
          <span
            className={`ml-2 text-base ${
              isProPlan ? "text-purple-100" : "text-gray-400"
            }`}
          >
            /month
          </span>
        </div>
      </div>

      {/* Description */}
      <p
        className={`text-sm mb-4 ${
          isProPlan ? "text-purple-100" : "text-gray-300"
        }`}
      >
        {plan.description}
      </p>

      {/* Action button - positioned before features */}
      <button
        onClick={() => {
          if (!isTeamDisabled) onClick();
        }}
        disabled={isLoading || isTeamDisabled}
        className={`w-full rounded-lg px-4 py-3 text-sm font-medium transition-all duration-300 mb-6 ${
          isProPlan
            ? "bg-white text-purple-600 hover:bg-purple-50"
            : isTeamDisabled
              ? "bg-gray-700 text-gray-500 cursor-not-allowed"
              : "bg-purple-600 text-white hover:bg-purple-700"
        }`}
      >
        {isLoading ? (
          <span className="flex items-center justify-center">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </span>
        ) : isTeamDisabled ? (
          "Coming Soon"
        ) : (
          "Try for Free"
        )}
      </button>

      {/* Tagline */}
      <p
        className={`text-sm font-medium mb-4 ${
          isProPlan ? "text-white" : "text-gray-200"
        }`}
      >
        {plan.tagline}
      </p>

      {/* Features list with icons */}
      <ul className="space-y-3 flex-1">
        {plan.features.map((feature, index) => {
          const IconComponent = FeatureIcons[feature.icon] || Sparkles;
          return (
            <li key={index} className="flex items-start">
              <IconComponent
                className={`h-4 w-4 mr-3 flex-shrink-0 mt-0.5 ${
                  isProPlan ? "text-purple-200" : "text-gray-400"
                }`}
              />
              <span
                className={`text-sm ${
                  isProPlan ? "text-purple-50" : "text-gray-300"
                }`}
              >
                {feature.text}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export function PricingSection() {
  const [billingCycle, setBillingCycle] = useState("monthly");
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const handlePlanClick = (planId) => {
    // If user is not logged in, redirect to signup with plan info
    if (!user) {
      navigate(`/auth/signup?plan=${planId}&billing=${billingCycle}`, {
        replace: true,
      });
      return;
    }

    // If logged in, go to pricing page to complete checkout
    navigate("/pricing");
  };

  return (
    <section id="pricing" className="py-8 sm:py-12 bg-transparent relative">
      <div className="container mx-auto px-4">
        <div className="text-center mb-6 sm:mb-8">
          {/* Simple header */}
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">
            Simple pricing
          </h2>
          <p className="text-muted-foreground text-sm sm:text-base max-w-xl mx-auto mb-6">
            Choose the plan that fits your needs. No hidden costs.
          </p>

          {/* Billing Cycle Toggle */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex p-1 rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setBillingCycle("monthly")}
                className={`px-5 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                  billingCycle === "monthly"
                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                    : "bg-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setBillingCycle("yearly")}
                className={`px-5 py-1.5 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                  billingCycle === "yearly"
                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                    : "bg-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                }`}
              >
                Yearly
                <span className="text-green-500 dark:text-green-400 text-xs font-normal">
                  Save 10%
                </span>
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-3xl mx-auto px-2 sm:px-4">
          <PricingCard
            plan={PLANS.PRO}
            billingCycle={billingCycle}
            onClick={() => handlePlanClick(PLANS.PRO.id)}
          />
          <PricingCard
            plan={PLANS.TEAM}
            billingCycle={billingCycle}
            onClick={() => handlePlanClick(PLANS.TEAM.id)}
          />
        </div>
        <div className="text-center mt-6 text-xs text-muted-foreground px-4">
          Prices shown do not include applicable taxes. Your subscription will
          renew automatically.
        </div>
      </div>
    </section>
  );
}

export default PricingSection;
